import { Settings, Bell, Menu, LogOut, FlaskConical } from "lucide-react";
import { useState, lazy, Suspense } from "react";
import { useLocation, Link } from "react-router-dom";
import { PlanBadge } from "@/components/billing/PlanBadge";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { ThemeDropdown } from "@/components/ThemeDropdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNotificationItems } from "@/hooks/useNotificationItems";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { getEnvironmentBadge } from "@/lib/environmentBadge";
import { navigationItems } from "@/lib/navigation";
import { usePerformanceMeasure } from "@/perf/usePerformanceMeasure";

const MobileNavigationSheet = lazy(() =>
  import("@/components/MobileNavigationSheet").then((module) => ({
    default: module.MobileNavigationSheet,
  })),
);

export const Header = () => {
  const { subscriptions } = useSubscriptions();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const environmentBadge = getEnvironmentBadge();

  // Performance measurement for Header renders
  usePerformanceMeasure("Header");

  // Fetch notifications using the same logic as the notifications page
  const { payments } = usePaymentHistory();
  const notificationItems = useNotificationItems(subscriptions, payments);
  const urgentNotifications = notificationItems.filter(
    (n) => n.severity === "urgent",
  );
  const hasUrgentNotifications = urgentNotifications.length > 0;
  const totalNotifications = notificationItems.length;

  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-40 border-b backdrop-blur supports-[backdrop-filter]:bg-background/60 ${
        environmentBadge.isVisible
          ? "border-warning/40 bg-warning/10"
          : "border-border/40 bg-background/95"
      }`}
    >
      <div className="container flex h-16 items-center justify-between">
        {/* Mobile Layout */}
        {isMobile ? (
          <>
            {/* Mobile Left Side */}
            <div className="flex min-w-0 items-center space-x-3">
              {/* Hamburger Menu */}
              <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="default"
                    className="h-10 w-10 p-0"
                    aria-label="Open navigation menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <Suspense fallback={<div className="w-80 p-4">Loading...</div>}>
                  <MobileNavigationSheet
                    hasUrgentNotifications={hasUrgentNotifications}
                    onMenuItemClick={handleMenuItemClick}
                  />
                </Suspense>
              </Sheet>

              {/* Logo */}
              <div className="flex min-w-0 items-center space-x-2">
                <img
                  src="/nokosha-fox-icon.png"
                  alt="Nokosha"
                  className="h-7 w-7"
                />
                <div className="flex min-w-0 items-center space-x-2">
                  <h1 className="truncate text-lg font-bold text-foreground">
                    Nokosha
                  </h1>
                  {environmentBadge.isVisible && (
                    <Badge
                      variant={environmentBadge.variant}
                      className="gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide"
                    >
                      <FlaskConical className="h-3 w-3" />
                      {environmentBadge.label}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile Right Side - Essential Actions */}
            <div className="flex items-center space-x-1">
              <div className="shrink-0">
                <PlanBadge />
              </div>

              {/* Notifications */}
              <Button
                variant="ghost"
                size="default"
                className="relative h-10 w-10 p-0"
                title="Notifications"
                asChild
              >
                <Link to="/notifications">
                  <Bell className="h-5 w-5" />
                  {hasUrgentNotifications && (
                    <div className="absolute -right-1 -top-1 h-3 w-3 animate-glow-pulse rounded-full bg-destructive" />
                  )}
                </Link>
              </Button>

              {/* Feedback */}
              <FeedbackDialog size="default" />

              {/* Settings */}
              <Button
                variant="ghost"
                size="default"
                className="h-10 w-10 p-0"
                title="Settings"
                asChild
              >
                <Link to="/settings">
                  <Settings className="h-5 w-5" />
                </Link>
              </Button>

              {/* Theme Toggle */}
              <ThemeDropdown />
            </div>
          </>
        ) : (
          /* Desktop Layout */
          <>
            {/* Logo & Title */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <img
                  src="/nokosha-fox-icon.png"
                  alt="Nokosha - Your wise fox companion"
                  className="h-8 w-8"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold text-foreground">
                      Nokosha
                    </h1>
                    {environmentBadge.isVisible && (
                      <Badge
                        variant={environmentBadge.variant}
                        className="gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide"
                      >
                        <FlaskConical className="h-3 w-3" />
                        {environmentBadge.label}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Your wise companion for subscriptions
                  </p>
                </div>
              </div>

              {/* Desktop Navigation */}
              <nav className="flex items-center space-x-1">
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    variant={
                      location.pathname === item.path ? "secondary" : "ghost"
                    }
                    size="sm"
                    asChild
                  >
                    <Link
                      to={item.path}
                      className="flex items-center space-x-2"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  </Button>
                ))}
              </nav>
            </div>

            {/* Desktop Action Buttons */}
            <div className="flex items-center space-x-2">
              {/* User Email */}
              <span className="hidden text-sm text-muted-foreground lg:block">
                {user?.email}
              </span>
              <PlanBadge />

              {/* Notifications */}
              <Button
                variant="ghost"
                size="icon"
                className="relative h-9 w-9"
                title={
                  totalNotifications > 0
                    ? `${totalNotifications} notification${totalNotifications !== 1 ? "s" : ""}`
                    : "Notifications"
                }
                asChild
              >
                <Link to="/notifications" aria-label="Open notifications">
                  <Bell className="h-4 w-4" />
                  {hasUrgentNotifications && (
                    <span
                      className="absolute -right-1 -top-1 h-3 w-3 animate-glow-pulse rounded-full bg-destructive"
                      aria-hidden="true"
                    />
                  )}
                </Link>
              </Button>

              {/* Feedback */}
              <FeedbackDialog size="sm" />

              {/* Settings */}
              <Button variant="ghost" size="sm" title="Settings" asChild>
                <Link to="/settings">
                  <Settings className="h-4 w-4" />
                </Link>
              </Button>

              {/* Theme Toggle */}
              <ThemeDropdown size="sm" />

              {/* Logout */}
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await signOut();
                }}
                title="Sign out"
                className="text-destructive hover:text-destructive"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </div>
    </header>
  );
};
